import React, { useEffect, useState } from 'react'
import {
  IntegrationContainer,
  API,
  Institutions,
  WhiteLabel
} from '@/styles/pages/integration'

import Public from '@/helpers/PageTypes/Public'
import { useWindowSize } from '@/contexts/WindowSizeContext'

import SEO from '@/components/SEO'
import HeaderUnsigned from '@/components/LandingPage/Header'
import FooterUnsigned from '@/components/LandingPageV2/Shared/Footer'
import BackToTopButton from '@/components/BackToTopButton'
import BannerIntro from '@/components/Banners/Intro'
import BannerOutro from '@/components/Banners/Outro'
import ImageNext from '@/components/ImageNext'

import { PAGE_PATHS } from '@/services/paths'
import { EVENT_KEYS, sendToDataLayer } from '@/services/events'

import { useTranslation } from 'react-i18next'
import { useRouter } from 'next/router'

const baseImgURL = `${process.env.CDN_URL}/public/contents`
const baseIconURL = `${process.env.CDN_URL}/public/icons`

const cellImg = `${baseImgURL}/cell.webp`
const apiImg = `${baseImgURL}/api.webp`
const bioImg = `${baseImgURL}/bioatlas.webp`

const pharmaIcon = `${baseIconURL}/pharma.svg`
const univerIcon = `${baseIconURL}/university.svg`
const hospitIcon = `${baseIconURL}/hospital.svg`
const editorIcon = `${baseIconURL}/editor.svg`

const IntegrationV2 = () => {
  const { t } = useTranslation()
  const { windowSize } = useWindowSize()
  const router = useRouter()

  const [pageTitle, setPageTitle] = useState(' | Integration')
  const [bannerTitle, setBannerTitle] = useState('')
  const [bannerSubtitle, setBannerSubtitle] = useState('')
  const [apiTitle, setApiTitle] = useState('')
  const [apiSubtitle, setApiSubtitle] = useState('')
  const [institutionsTitle, setInstitutionsTitle] = useState('')
  const [institutionsItem1, setInstitutionsItem1] = useState('')
  const [institutionsItem2, setInstitutionsItem2] = useState('')
  const [institutionsItem3, setInstitutionsItem3] = useState('')
  const [institutionsItem4, setInstitutionsItem4] = useState('')
  const [whiteLabelTitle, setWhiteLabelTitle] = useState('')
  const [whiteLabelSubtitle, setWhiteLabelSubtitle] = useState('')
  const [buttonsDemo, setButtonsDemo] = useState('Demo')
  const [isScreenDesktopSized, setIsScreenDesktopSized] = useState(true)

  useEffect(() => {
    setPageTitle(` | ${t('titles.integration')}`)
    setBannerTitle(t('integration.banner.title'))
    setBannerSubtitle(t('integration.banner.subtitle'))
    setApiTitle(t('integration.api.title'))
    setApiSubtitle(t('integration.api.subtitle'))
    setInstitutionsTitle(t('integration.institutions.title'))
    setInstitutionsItem1(t('integration.institutions.item1'))
    setInstitutionsItem2(t('integration.institutions.item2'))
    setInstitutionsItem3(t('integration.institutions.item3'))
    setInstitutionsItem4(t('integration.institutions.item4'))
    setWhiteLabelTitle(t('integration.whiteLabel.title'))
    setWhiteLabelSubtitle(t('integration.whiteLabel.subtitle'))
    setButtonsDemo(t('buttons.demo'))
  }, [t])

  useEffect(() => {
    if (!windowSize.width) return

    const isDesktop = windowSize.width > 900
    setIsScreenDesktopSized(isDesktop)
  }, [windowSize])

  useEffect(() => {
    if (!router) return

    const sectionID = router.query.section
    if (sectionID) {
      setTimeout(
        () => document.getElementById(sectionID).scrollIntoView(),
        1000
      )
    }
  }, [router])

  const renderInstitutionItem = (icon, text) => {
    return (
      <div className="item">
        <div className="icon-container mx-auto flexbox h-centered">
          <ImageNext
            src={icon}
            alt=""
            width={44}
            height={45}
            autoSize={false}
          />
        </div>
        <p>{text}</p>
      </div>
    )
  }

  const requestDemoCallback = () => {
    sendToDataLayer({
      event: EVENT_KEYS.requestDemo
    })
  }

  return (
    <Public>
      <SEO title={pageTitle} />
      <HeaderUnsigned pageName="integration" />
      <IntegrationContainer>
        <BannerIntro
          bg={cellImg}
          bgWidth={480}
          bgHeight={493}
          title={bannerTitle}
          subtitle={bannerSubtitle}
        />

        {isScreenDesktopSized ? (
          <API id="section_api" className="flexbox h-around desktop">
            <div className="img-container flexbox">
              <ImageNext
                src={apiImg}
                alt=""
                width={465}
                height={463}
                style={{ width: '100%', height: 'auto' }}
              />
            </div>
            <div className="text-container">
              <h2 className="title">{apiTitle}</h2>
              <p>{apiSubtitle}</p>
              <a
                href={PAGE_PATHS.contact}
                id="btn_contact"
                className="btn blue lg"
                onClick={requestDemoCallback}
              >
                {buttonsDemo}
              </a>
            </div>
          </API>
        ) : (
          <API id="section_api" className="mobile">
            <div className="text-container">
              <h2 className="title">{apiTitle}</h2>
              <p>{apiSubtitle}</p>
            </div>
            <div className="img-container flexbox mx-auto">
              <ImageNext
                src={apiImg}
                alt=""
                width={465}
                height={463}
                style={{ width: '100%', height: 'auto' }}
              />
            </div>
          </API>
        )}

        {isScreenDesktopSized ? (
          <Institutions id="section_institutions" className="desktop">
            <h2 className="title">{institutionsTitle}</h2>

            <div className="svg-container left">
              <svg
                xmlns="https://www.w3.org/2000/svg"
                width="100%"
                height="100%"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
              >
                <title>SVG Curve</title>
                <desc>Decorative curve in SVG</desc>
                <path d="M100,100 C0,100 -10,30 35,5" />
                {/* ry is 65% of rx with current proportions */}
                <ellipse cx="35" cy="5" rx="1" ry="0.65" />
                Your browser does not support inline SVG.
              </svg>
            </div>

            <div className="item-container flexbox v-centered h-spaced mx-auto">
              {renderInstitutionItem(pharmaIcon, institutionsItem1)}
              {renderInstitutionItem(univerIcon, institutionsItem2)}
              {renderInstitutionItem(hospitIcon, institutionsItem3)}
              {renderInstitutionItem(editorIcon, institutionsItem4)}
            </div>

            <div className="svg-container right">
              <svg
                xmlns="https://www.w3.org/2000/svg"
                width="100%"
                height="100%"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
              >
                <title>SVG Curve</title>
                <desc>Decorative curve in SVG</desc>
                <path d="M0,0 C100,0 110,70 65,95" />
                {/* ry is 54% of rx with current proportions */}
                <ellipse cx="65" cy="95" rx="1" ry="0.54" />
                Your browser does not support inline SVG.
              </svg>
            </div>
          </Institutions>
        ) : (
          <Institutions id="section_institutions" className="mobile">
            <h2 className="title">{institutionsTitle}</h2>
            <div className="item-container flexbox v-centered h-spaced">
              {renderInstitutionItem(pharmaIcon, institutionsItem1)}
              {renderInstitutionItem(univerIcon, institutionsItem2)}
            </div>
            <div className="item-container flexbox v-centered h-spaced">
              {renderInstitutionItem(hospitIcon, institutionsItem3)}
              {renderInstitutionItem(editorIcon, institutionsItem4)}
            </div>
          </Institutions>
        )}

        {isScreenDesktopSized ? (
          <WhiteLabel
            id="section_whitelabel"
            className="flexbox h-spaced desktop"
          >
            <div className="text-container">
              <h2 className="title">{whiteLabelTitle}</h2>
              <p>{whiteLabelSubtitle}</p>
            </div>
            <div className="img-container flexbox">
              <ImageNext
                src={bioImg}
                alt=""
                width={785}
                height={475}
                style={{ width: '100%', height: 'auto' }}
              />
            </div>
          </WhiteLabel>
        ) : (
          <WhiteLabel id="section_whitelabel" className="mobile">
            <div className="text-container">
              <h2 className="title">{whiteLabelTitle}</h2>
              <p>{whiteLabelSubtitle}</p>
            </div>
            <div className="img-container flexbox">
              <ImageNext
                src={bioImg}
                alt=""
                width={785}
                height={475}
                style={{ width: '100%', height: 'auto' }}
              />
            </div>
          </WhiteLabel>
        )}

        <BannerOutro />
      </IntegrationContainer>
      <FooterUnsigned />
      <BackToTopButton threshold={600} />
    </Public>
  )
}

export default IntegrationV2
